import {Heading} from "@chakra-ui/react";
import React, {forwardRef, useEffect, useImperativeHandle, useRef, useState} from "react";
import InputSelect from "./InputSelect";
import InputText from "./InputText";
import InputDate from "./InputDate";
import {TypeUnitRepository} from "../../repository/TypeUnitRepository";
import Option from '../../interfaces/Option'
import {useAuth} from "../../auth/AuthProvider";
import {useApiProvider} from "../../api/ApiProvider";
import InputRef from "../../interfaces/InputRef";
import SubtleButton, {SubtleButtonColor} from "./SubtleButton";
import {
    ScoutCensusSummaryActiveAssignmentsForm
} from "../views/protected/scoutCensus/active/ActiveScoutCensusSummaryForm";
import ErrorMessage from "./ErrorMessage";

interface FunctionAssignmentHandle {
    validate: () => boolean;
    value: () => ScoutCensusSummaryActiveAssignmentsForm | null;
}

interface FunctionAssignmentProps {
    index: number;
    functionsCannotReceiveExtendedInfo: string[];
    functionsRequiresExtendedInfo: string[];
    onRemove: () => void;
}

const FunctionForm = forwardRef<FunctionAssignmentHandle, FunctionAssignmentProps>(
    (props, ref) => {
    
    const [typeUnit, setTypeUnit] = useState<Option[]>();
    const [functions, setFunctions] = useState<Option[]>();
    const [selectedScoutDistrict, setSelectedScoutDistrict] = useState<Option | undefined>();
    const [selectedTypeUnit, setSelectedTypeUnit] = useState<Option | undefined>(undefined);
    const [selectedFunction, setSelectedFunction] = useState<Option | undefined>(undefined);
    const [extendedInformation, setExtendedInformation] = useState<string | null>(null);

    const { user } = useAuth();
    const provider = useApiProvider();
    const [areScoutDistrictsFetched, setAreScoutDistrictsFetched] = useState<boolean>(false);
    const [scoutDistricts, setScoutDistricts] = useState<Option[]>();

    useEffect(() => {
        if (selectedTypeUnit?.value !== 'hufiec') return;
        provider.getScoutDistricts()
            .then(data => {setScoutDistricts(data.map(unit => {
                return {
                    label: unit.Name,
                    value: unit.Id,
                } as Option
            }));
                console.log("data: ", data)
            })
            .catch(err => console.error(err))
            .finally(() => setAreScoutDistrictsFetched(true));
        console.log("scoutRegions: " + scoutDistricts);
    }, [selectedTypeUnit]);
        
    const repository: TypeUnitRepository = new TypeUnitRepository();
    const typeUnitRef = useRef<InputRef>(null);
    const functionRef = useRef<InputRef>(null);
    const functionExtendedInformationRef = useRef<InputRef>(null);
    const decreeRef = useRef<InputRef>(null);
    const startDateRef = useRef<InputRef>(null);
    const endDateRef = useRef<InputRef>(null);
    const unitNameRef = useRef<InputRef>(null);

    const [signUpError, setSignUpError] = useState<string>("");

    const validateValue = () => {
        if (!typeUnitRef.current?.validate()
            || !functionRef.current?.validate()
            || !startDateRef.current?.validate()
            || !endDateRef.current?.validate()) return false;

        if (selectedTypeUnit?.value === 'hufiec' && !unitNameRef.current?.validate()) {
            return false;
        }

        if (!props.functionsCannotReceiveExtendedInfo.includes(selectedFunction?.value ?? '')) {
            if (props.functionsRequiresExtendedInfo.includes(selectedFunction?.value ?? '') && !functionExtendedInformationRef.current?.validate())
                return false;
            if (functionExtendedInformationRef.current?.validate() && functionExtendedInformationRef.current.value() != '') {
                setExtendedInformation(functionExtendedInformationRef.current.value());
                return true;
            }
        }

        if (props.functionsRequiresExtendedInfo.includes(selectedFunction?.value ?? '')
            && !functionExtendedInformationRef.current?.validate()) {
            return false;
        }

        var endDate = endDateRef?.current?.value() ?? '';
        var startDate = startDateRef?.current?.value() ?? '';
        if (new Date(startDate) > new Date(endDate)) {
            setSignUpError("Data rozpoczęcia musi być wybrana przed datą zakończenia.");
            return false;
        }

        return true;
    };

    useImperativeHandle(ref, () => ({
        validate: () => validateValue(),
        value: () => {
            if (props.functionsCannotReceiveExtendedInfo.includes(selectedFunction?.value ?? ''))
                return new ScoutCensusSummaryActiveAssignmentsForm ({
                    isMain: props.index == 0,
                    assignedFunction: selectedFunction?.value ?? '',
                    startDate: startDateRef.current?.value() ?? '',
                    endDate: endDateRef.current?.value() ?? '',
                    decree: decreeRef?.current?.value() ?? '',
                    unit: selectedScoutDistrict?.value ?? (selectedTypeUnit?.value ?? ''),
                    unitName: selectedScoutDistrict?.label ?? '',
                })
            
            if (props.functionsRequiresExtendedInfo.includes(selectedFunction?.value ?? '') && 
                !functionExtendedInformationRef.current?.validate())
                    return null;
            return new ScoutCensusSummaryActiveAssignmentsForm ({
                isMain: props.index == 0,
                assignedFunction: selectedFunction?.value ?? '',
                startDate: startDateRef.current?.value() ?? '',
                endDate: endDateRef.current?.value() ?? '',
                decree: decreeRef?.current?.value() ?? '',
                additionalInformation: functionExtendedInformationRef?.current?.value() ?? '',
                unit: selectedScoutDistrict?.value ?? (selectedTypeUnit?.value ?? ''),
                unitName: selectedScoutDistrict?.label ?? '',
            })
        }
    }));

    useEffect(() => {
        repository.getTypes().then((list: Option[]) => setTypeUnit(list));
    }, []);

    const handleTypeChange = (value: Option | undefined) => {
        if (value != undefined) {
            repository.getSubTypes(value.value).then((list: Option[]) => setFunctions(list));
        }
        setSelectedTypeUnit(value);
    };

    return (
        <div className="function-form">
            <Heading marginTop={7}
                     as="h4"
                     size="md"
            >
                {props.index == 0 ? "Główny przydział służbowy" : "Przydział służbowy"}
            </Heading>
            <InputSelect
                ref={typeUnitRef}
                initValue={undefined}
                label="Przydział służbowy"
                placeholderText="Wybierz opcję z listy"
                options={typeUnit ?? []}
                isRequired
                getSelectedOption={handleTypeChange}
            />
            {selectedTypeUnit?.value === 'hufiec' && ( areScoutDistrictsFetched ?
                <InputSelect
                    ref={unitNameRef}
                    initValue={undefined}
                    label="Hufiec"
                    placeholderText="Wybierz hufiec z listy"
                    options={scoutDistricts ?? []}
                    isRequired
                    getSelectedOption={setSelectedScoutDistrict}
                /> :
                <>Pobieranie danych...</>
            )}
            <InputSelect
                ref={functionRef}
                initValue={undefined}
                label="Funkcja"
                placeholderText="Wybierz opcję z listy"
                options={functions ?? []}
                isRequired={true}
                getSelectedOption={setSelectedFunction}
            />
            {!props.functionsCannotReceiveExtendedInfo.includes(selectedFunction?.value ?? '') && (
                <InputText
                    ref={functionExtendedInformationRef}
                    label={'Dodatkowe informacje o funkcji'}
                    initValue={''}
                    placeholderText={(selectedFunction?.value === 'drużynowy' || selectedFunction?.value === 'przyboczny')
                        ? '13 Gdyńska Drużyna Harcerzy im. "Zawiszy Czarnego"'
                        : (selectedFunction?.value === 'inne' ? 'Kanclerz Gdyni' : 'referent ds. harcerzy')}
                    isRequired={props.functionsRequiresExtendedInfo.includes(selectedFunction?.value ?? '')}
                    rows={3}
                    maxLength={500}
                />
            )}
            <InputText 
                ref={decreeRef}
                label={'Numer rozkazu nadającego funkcję'}
                placeholderText={"L 10/2024"}
                isRequired={false}
                maxLength={20}
            />
            <InputDate
                ref={startDateRef}
                initValue={''}
                label={'Początek pełnienia funkcji'}
                isRequired={true}
            />
            <InputDate
                ref={endDateRef}
                initValue={''}
                label={'Przewidywana data zakończenia pełnienia funkcji'}
                isRequired={true}
            />
            <ErrorMessage text={signUpError} />
            {props.index !== 0 && (
                <SubtleButton  text="Usuń funkcję" function={props.onRemove} color={SubtleButtonColor.RED}/>
            )}
        </div>
    );
});

interface MultiFunctionFormHandle {
    validate: () => boolean;
    value: () => ScoutCensusSummaryActiveAssignmentsForm[];
}

interface MultiFunctionFormProps {
    functionsCannotReceiveExtendedInfo: string[];
    functionsRequiresExtendedInfo: string[];
}

const MultiFunctionForm = forwardRef<MultiFunctionFormHandle, MultiFunctionFormProps>(
    (props, ref) => {
        
    const [functionsList, setFunctionsList] = useState<{ id: string; ref: React.RefObject<FunctionAssignmentHandle> }[]>([
        { id: crypto.randomUUID(), ref: React.createRef() }
    ]);
    const validateValue = () => {
        return functionsList.every((formRef) => formRef.ref.current?.validate());
    };

    useImperativeHandle(ref, () => ({
        validate: () => validateValue(),
        value: () => {
            const result = functionsList
                .map((formRef, index) => {
                    return formRef.ref.current?.value();
                })
                .filter(
                    (val): val is ScoutCensusSummaryActiveAssignmentsForm => val !== undefined
                )
            return result;
        }
    }));

    const addFunction = () => {
        setFunctionsList([...functionsList, { id: crypto.randomUUID(), ref: React.createRef() }]);
    };

    const removeFunction = (id: string) => {
        setFunctionsList(functionsList.filter((form) => form.id !== id));
    };
    
    return (
        <div>
            {functionsList.map((form, index) => (
                <FunctionForm
                    key={form.id}
                    ref={form.ref}
                    index={index}
                    functionsCannotReceiveExtendedInfo={props.functionsCannotReceiveExtendedInfo}
                    functionsRequiresExtendedInfo={props.functionsRequiresExtendedInfo}
                    onRemove={() => removeFunction(form.id)}
                />
            ))}
            <SubtleButton  text="Dodaj kolejną funkcję" function={addFunction} color={SubtleButtonColor.BLUE}/>
        </div>
    );
});

export default MultiFunctionForm;
